import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userService } from '../api/users';
import { User, ArrowLeft, FileText, CheckCircle, Clock, XCircle } from 'lucide-react';
import StatsCard from '../components/ui/StatsCard';
import { useTranslation } from 'react-i18next';

export default function TutorDetailsPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [tutor, setTutor] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [userData, statsData] = await Promise.all([
                    userService.getById(id),
                    userService.getUserStats(id)
                ]);
                setTutor(userData);
                setStats(statsData);
            } catch (err) {
                console.error("Failed to fetch tutor details", err);
                setError(t('error_loading_tutor'));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchData();
        }
    }, [id, t]);

    if (loading) return <div className="p-8 text-center text-gray-500">{t('loading')}</div>;
    if (error) return <div className="p-8 text-center text-red-500">{error}</div>;
    if (!tutor) return <div className="p-8 text-center">{t('tutor_not_found')}</div>;

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-500 hover:text-primary-600 transition-colors"
            >
                <ArrowLeft className="w-5 h-5 mr-2" />
                {t('back')}
            </button>

            {/* Header / Profile Card */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-center md:items-start md:space-x-8">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-md mb-4 md:mb-0">
                    {tutor.avatar ? (
                        <img src={tutor.avatar} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <User className="w-16 h-16" />
                        </div>
                    )}
                </div>
                <div className="text-center md:text-left space-y-2">
                    <h1 className="text-3xl font-bold text-gray-900">
                        {tutor.first_name} {tutor.last_name}
                    </h1>
                    <p className="text-lg text-gray-500">@{tutor.username}</p>
                    <div className="inline-block px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium capitalize">
                        {tutor.role || 'Tutor'}
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">{t('statistics')}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatsCard
                        title={t('stats_total')}
                        value={stats?.total_reports || 0}
                        icon={FileText}
                        color="blue"
                    />
                    <StatsCard
                        title={t('stats_approved')}
                        value={stats?.approved_reports || 0}
                        icon={CheckCircle}
                        color="green"
                    />
                    <StatsCard
                        title={t('stats_pending')}
                        value={stats?.pending_reports || 0}
                        icon={Clock}
                        color="yellow"
                    />
                    <StatsCard
                        title={t('stats_rejected')}
                        value={stats?.rejected_reports || 0}
                        icon={XCircle}
                        color="red"
                    />
                </div>
            </div>
        </div>
    );
}
